import { defineStore } from 'pinia';
import { createVNode, inject, render } from 'vue';
import Tracker from '@nio-fe/tracker-sdk';
// import SensorsTracker from '@nio-fe/nio-tracker-web';
import {
  getTitMicroId,
  formatMicroData,
  getFullPathFromLocation,
} from '@/utils/router';
import qs from 'qs';
import { getCustomConfig, setCustomConfig } from '@/api/client/hebeClient';
import useGlobalStore from './globalStore';
import { THEME_CODE } from '@/enums/settingEnum';
import { setTheme } from '@/utils/common';
import OverloadMask from '@/components/OverloadMask/OverloadMask.vue';
import type {
  MicroData,
  SortingItem,
  TabItem,
  MicroAppTabMapping,
} from '@/api/model/globalModel';
import { loadMicroApp, prefetchApps } from '@nio-fe/mate-qiankun';
import generateMicroProps from '@/utils/micro/generateMicroProps';
import { groupBy, last, sortBy } from 'lodash-es';
import { $message } from '@nio-fe/lego';
import { getVueRouter } from '@/router';
import Cookies from 'js-cookie';
import {
  MATE_LANE_CODE,
  MATE_LANE_CODE_FX,
  MATE_PRECACHE_MICRO,
} from '@/enums';
import { UpdateMicroData } from '@/api/model/globalModel';
import { uniqBy } from 'lodash-es';
import { useGlobalRouteStore } from '@/store/globalRouteStore';
import { getBrowserType } from '@/utils';
import NProgress from 'nprogress';
import { revalidateCache } from '@/utils/revalidate_cache';

// 临时使用，用户没有自定义过 应用列表，默认如下
const generateDefaultFrequentlyAppsList = () => [
  { biz_code: 'tasks', sort: 0 },
  { biz_code: 'calendar', sort: 1 },
  { biz_code: 'message', sort: 2 },
  { biz_code: 'colleague', sort: 3 },
];

const generateFetch = (): any => {
  return (url: string, opts: any) => {
    // 给指定的微应用 entry 开启跨域请求
    const withCookies = /http/.test(url as string)
      ? /^(page-gateway|fx)(-(local|dev|stg|test|prod))?(-eu)?.nioint.com(:\w+)?$/.test(
          new URL(url).host
        )
      : false;
    let options = {
      ...opts,
    };
    if (withCookies) {
      options = {
        ...opts,
        mode: 'cors',
        credentials: 'include',
      };
    }
    const lane = Cookies.get(MATE_LANE_CODE_FX);
    if (lane) {
      options.headers = options.headers || {};
      options.headers['Lane'] = lane;
      options.headers['x-fx-lane-fallback'] = 'true';
    }
    return window.fetch(url, options);
  };
};
// @ts-ignore
const useMatestoreStore = defineStore({
  id: 'matestore',
  state: () => ({
    options: {
      disablePrefetch:
        getBrowserType() !== 'Chrome' ||
        Cookies.get(MATE_PRECACHE_MICRO) !== '1',
    } as {
      disablePrefetch?: boolean;
    },
    showIm: false,
    overloadingMaskContainerEl: null as HTMLElement | null,
    // 源数据
    sourceData: [] as MicroData[],
    // 全部微应用
    microList: [] as MicroData[],
    // 菜单拖拽-存储使用
    draggingData: null as any,

    microTabMapping: {} as MicroAppTabMapping,

    latestOpenedMicroAppName: undefined as undefined | string,

    openedTemporaryMicroAppNames: [] as string[],

    sortingList: [] as SortingItem[],

    specialMicroApps: [
      {
        name: 'message',
        appCode: 'message',
        identifier: 'message',
        isSpecialItem: true,
        sort: 2,
        icon: 'message_filled',
        label: '消息',
        group: 'frequently',
        microType: 'micro',
      },
      {
        name: 'dashboard',
        appCode: 'dashboard',
        isSpecialItem: true,
        sort: 5,
        icon: 'dashboard_filled',
        fullPath: '/dashboard',
        label: '仪表板',
        group: 'frequently',
        microType: 'micro',
      },
    ],

    temporaryOpenedAppList: [] as { name: string; activateAt?: number }[],
  }),
  getters: {
    microMap: (state) =>
      state.microList.reduce<Record<string, MicroData>>((r, i) => {
        r[i.name] = i;
        return r;
      }, {}),
    // @ts-ignore
    currentApp: (state) => state.microMap[state.latestOpenedMicroAppName],
    imMicro: (state) => {
      // @ts-ignore
      return state.microMap['im'];
    },
    sortingMap: (state) =>
      state.sortingList.reduce<Record<string, SortingItem>>((r, i) => {
        r[i.name] = i;
        return r;
      }, {}),
    microRouteHistory: (state) => {
      // @ts-ignore
      return state.microTabMapping[state.currentApp?.name] || [];
    },
    dashboardMicroApps: (state) =>
      state.microList.filter((i) => i.microType === 'dashboard'),

    microComponent: (state) =>
      state.microList.filter((i) => i.microType === 'component'),

    frequentlyZoneMicroApps: (state) =>
      // only for sidebar
      // @ts-ignore
      sortBy(
        state.sortingList.filter((i) => i.microDataRef.group === 'frequently'),
        (i) => i.sortForFreq
      ) as SortingItem[],
    temporaryZoneMicroApps: (state) =>
      // only for sidebar
      // @ts-ignore
      sortBy(
        state.sortingList.filter(
          (i) =>
            i.microDataRef.activateAt && i.microDataRef.group !== 'frequently'
        ),
        (i) => i.microDataRef.activateAt
      ).reverse() as SortingItem[],
    moreZoneMicroApps: (state) =>
      // only for sidebar
      sortBy(
        state.sortingList.filter((i) => i.microDataRef.group === 'more'),
        (i) => i.sort
      ) as SortingItem[],
    microMenu: (state) =>
      state.microList.filter(
        (i) => !['dashboard', 'component'].includes(i.microType as string)
      ),
    hasGlobalSearch: (state) => {
      return state.microList.some((i) => i.name === 'search/appSearch');
    },
    hasNotice: (state) => {
      return state.microList.some((i) => i.name === 'im/notice');
    },
  },
  actions: {
    generateTemporaryOpenedAppList() {
      this.temporaryOpenedAppList = this.temporaryZoneMicroApps.map((i) => ({
        name: i.name as string,
        activateAt: i.microDataRef.activateAt,
      }));
    },

    hasMicroAppAuthority(microData: MicroData, authorityMapping: any) {
      return (
        !microData.identifier ||
        !!authorityMapping[microData.identifier as string]
      );
    },

    resortAll(sortingList) {
      const grouped = groupBy(sortingList, (i) => i.microDataRef.group);
      Object.keys(grouped).forEach((type) => {
        const list = sortBy(grouped[type], (i) =>
          type === 'frequently' ? i.sortForFreq : i.sort
        );
        switch (type) {
          case 'frequently':
            list.forEach((i, index) => {
              i.sortForFreq = index;
              i.sort = undefined;
            });
            break;
          case 'more':
            list.forEach((i, index) => {
              i.sort = index;
              i.sortForFreq = undefined;
            });
            break;
        }
      });
    },

    /**
     * 初始化
     * @returns
     */
    async initMicro(options: { disablePrefetch?: boolean } = {}) {
      this.options = {
        ...this.options,
        ...options,
      };
      const all: any[] = [];
      const globalStore = useGlobalStore();
      for (const item of this.sourceData || []) {
        const microItems = formatMicroData(item).filter((i) => {
          if (!i.identifier) {
            return true;
          }
          return globalStore.hasAuthority(i.identifier);
        });
        microItems.forEach((microItem) => {
          // 兼容route唯一等 结构
          const { name, path } = microItem;
          const microId = getTitMicroId(path);
          const routeOption: any = { ...microItem, name, path, microId };
          if (all.find((i) => i.name === name)) {
            console.log('duplicate micro app =>', name, item);
            return;
          }
          // microType: matestore（matestore小程序），component（组件，例 用户详情）
          all.push(routeOption);
        });
      }
      this.microList = [...all];
      this.sortingList = this.microMenu.map((i, index) => ({
        name: i.name,
        sort: i.sort || index,
        sortForFreq: i.sort || index,
        sortForTemp: i.sort || index,
        microDataRef: i,
      }));
      const specialMicros = this.specialMicroApps
        .map((item) => ({
          name: item.name,
          sort: item.sort,
          sortForFreq: item.sort,
          sortForTemp: item.sort,
          microDataRef: item,
        }))
        .filter((i) => {
          if (i.name === 'dashboard') {
            return this.dashboardMicroApps.length > 0;
          } else if (i.name === 'message') {
            /*TODO: 这里需要考虑单品牌问题*/
            // @ts-ignore
            return globalStore.hasAuthority(i.microDataRef.identifier, [
              'nio',
              'alps',
            ]);
          } else {
            return true;
          }
        });
      // @ts-ignore
      this.sortingList = [...this.sortingList, ...specialMicros];
      this.sortingList = sortBy(
        this.sortingList,
        (i) => (i.sort as number) + 1
      );
      await this.initCustomConfig();
      // 禁用全局预加载
      // if (!this.options.disablePrefetch) {
      this.precacheAllMicroApps();
      // }
    },
    /**
     * 初始化-个性配置
     */
    async initCustomConfig() {
      const userInfo = useGlobalStore().userInfo;

      const doRevalidate = (response: any) => {
        const customConfigs = response;

        let confList: any[] = [];
        let frequentlyList: any[] = generateDefaultFrequentlyAppsList();
        let moreList: any[] = [];
        if (response) {
          confList = customConfigs?.configs || [];
          for (const item of confList) {
            switch (item.type) {
              case 'frequently_used_app':
                if (item.list) {
                  frequentlyList = item.list.splice(1);
                }
                break;
              case 'more_tab':
                moreList = item.list;
                break;
            }
          }
        }

        let theme =
          confList.find((i) => i.type === 'theme_color')?.list?.[0]?.biz_code ||
          THEME_CODE.auroraBlue;
        useGlobalStore().theme = theme;
        setTheme(theme);
        frequentlyList.forEach((current) => {
          const microItem = this.sortingMap[current.biz_code];
          if (microItem) {
            microItem.sortForFreq = current.sort;
            microItem.microDataRef.sort = current.sort;
            microItem.microDataRef.group = 'frequently';
          }
        });

        moreList.forEach((current) => {
          const microItem = this.sortingMap[current.biz_code];
          if (microItem) {
            microItem.sort = current.sort;
            microItem.microDataRef.sort = current.sort;
            microItem.microDataRef.group = 'more';
          }
        });

        this.resortAll(this.sortingList);
        this.temporaryOpenedAppList.forEach((item) => {
          let micro: MicroData | undefined = this.microMap[item.name];
          if (!micro) {
            micro = this.specialMicroApps.find((i) => i.name === item.name) as
              | MicroData
              | undefined;
            if (!micro) {
              return;
            }
          }
          micro.activateAt = item.activateAt;
        });
      };
      let responseData: any;
      try {
        responseData = await revalidateCache(
          'getCustomConfig',
          getCustomConfig({
            types: 'frequently_used_app,more_tab,theme_color',
            module: 'titan_web',
            domain_account: userInfo.domainAccount,
          }).then((data) => data.data || { configs: [] }),
          (data) => {
            doRevalidate(data);
          },
          (error) => {
            console.error('getCustomConfig error', error);
          }
        );
      } catch (error) {
        console.error(error);
      }

      doRevalidate(responseData);
    },

    precacheAllMicroApps() {
      const shouldPrecacheApps = ['search/appSearch'];
      const qiankunMicroApps = this.microList
        .filter((i) => !i.isIframe)
        .filter((i) => shouldPrecacheApps.includes(i.name))
        .map((i) => this._formatQianKunMicroAppOptions(i));
      this._precacheMicroApps(qiankunMicroApps);
    },

    async changeMicroAppSorting(
      microAppName: string,
      fromIndex = 0,
      toIndex = 0,
      toMicroAppName: string,
      group: 'frequently' | 'more'
    ) {
      console.log(microAppName, fromIndex, '->', toIndex, group);
      const microApp = this.sortingMap[microAppName];

      if (!microApp) {
        return;
      }
      microApp.microDataRef.group = group;
      const removeMicroList = this.sortingList.filter(
        (i) => i.name !== microAppName
      );
      const grouped = groupBy(removeMicroList, (i) => i.microDataRef.group);
      Object.keys(grouped).forEach((type) => {
        const list = grouped[type];
        grouped[type] = sortBy(list, (i) => {
          {
            if (type === 'frequently') {
              return i.sortForFreq;
            } else {
              return i.sort;
            }
          }
        });
      });
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].splice(toIndex, 0, microApp);
      Object.keys(grouped).forEach((type) => {
        const list = grouped[type];
        list.forEach((i, index) => {
          if (type === 'frequently') {
            i.sortForFreq = index;
          } else {
            i.sort = index;
          }
        });
      });
      try {
        const configs = [
          {
            type: 'more_tab',
            list: this.moreZoneMicroApps.map((item) => ({
              sort: item.sort,
              identifier:
                (item?.microDataRef.identifier || item.name) + `_${item.sort}`,
              biz_code: item.name,
              structure: 'plugin-url',
              name: item?.microDataRef.label,
            })),
          },
          {
            type: 'frequently_used_app',
            list: [
              {
                sort: -1,
                identifier: 'empty',
                biz_code: '',
                structure: '',
                name: '',
              },
              ...(grouped['frequently'] || []).map((item) => ({
                sort: item.sortForFreq,
                identifier:
                  (item?.microDataRef.identifier || item.name) +
                  `_${item.sortForFreq}`,
                biz_code: item?.microDataRef.name,
                structure: 'plugin-url',
                name: item?.microDataRef?.label,
              })),
            ],
          },
        ];
        this.resortAll(this.sortingList);
        return setCustomConfig({
          module: 'titan_web',
          configs,
        })
          .then(() => {
            console.log('configs', configs);
          })
          .catch((e) => {
            $message.error(`更改失败: ${e.message}`);
          });
      } catch (e: any) {
        $message.error(`更改失败: ${e.message}`);
      }
    },
    // sdk tabs，修改tab参数
    updateMicro(microName: string, data: Partial<UpdateMicroData>) {
      const activeTab = this.findActiveTab(microName);
      let micro: MicroData | undefined = this.microMap[microName];
      if (![undefined, null].includes(data.badgeNum as any)) {
        if (microName === 'im') {
          micro = this.specialMicroApps.find((i) => i.name === 'message') as
            | MicroData
            | undefined;
        }
        if (micro) {
          micro.badgeNum = data.badgeNum;
        }
      }
      if (data.routeHash) {
        if (!/^#/.test(data.routeHash)) {
          data.routeHash = `#${data.routeHash}`;
        }
        if (data.params) {
          data.routeHash = `${data.routeHash}?${qs.stringify(data.params)}`;
        }
      }
      const fullPath = `/${microName}${data.routeHash}`;

      if (activeTab) {
        if (data.title) {
          activeTab.title = data.title;
        }
        if (data.icon) {
          activeTab.icon = data.icon;
        }
        if (data.routeHash) {
          activeTab.fullPath = fullPath;
        }
        /*uniqueTabs*/
        let tabs = this.microTabMapping[microName];
        tabs = uniqBy(tabs, 'fullPath');
        this.microTabMapping[microName] = tabs;
      }
      if (data.routeHash !== undefined) {
        useGlobalRouteStore().replace(fullPath);
      }
    },

    async resetMicroAppSorting() {
      try {
        const configs = [
          {
            type: 'more_tab',
            list: [],
          },
          {
            type: 'frequently_used_app',
            list: undefined,
          },
        ];
        await setCustomConfig({
          module: 'titan_web',
          configs,
        });
        console.log('configs', configs);
        $message.success(`个人设置重置成功`);
      } catch (e: any) {
        $message.error(`重置失败: ${e.message}`);
      }
    },

    /**
     * 添加子应用，判断hash是否重复
     * 兼容老titan
     * @param to
     * @param from
     */
    addMicroByRouter(to: any, from: any) {
      const toMicroApp = this.microMap[to.name];
      const fromMicroApp = this.microMap[from?.name];
      if (!toMicroApp) {
        /*TODO: should I inactive fromMicroApp?*/
        return;
      }
      // TODO: 为啥需要更新Hash?
      // this.updateMicro(to.name, { hash: to.hash });

      if (!this.microAppIsInFrequentlyZone(to.name)) {
        this.setMicroAppToTemporaryZone(to.name);
      }
      if (toMicroApp.multiple) {
        this.addMicroTabItem({
          microName: toMicroApp.name,
          path: to.path || (toMicroApp.path as string),
          hash: to.hash || (toMicroApp.hash as string),
          fullPath: to.fullPath || (toMicroApp.fullPath as string),
        });
      }
      // this.activateMicroApp(to.name);
      // if (fromMicroApp) {
      //   this.deactivateMicroApp(fromMicroApp.name);
      // }
    },
    // /**
    //  * 添加临时区域
    //  * @TODO 重复添加逻辑 判断常应用是否已打开
    //  */
    // async addMicroTemporary(data: any) {
    //   // 当前打开子应用应用id
    //   const microId = getTitMicroId();
    //   const microData = { microId, ...data };
    //   this.currentApp = microData;
    //   this.microTemporary.push(microData);
    // },
    // // 关闭 临时区域
    // removeMicroTemporary(microId: string) {
    //   const index = findIndex(this.microTemporary, { microId });
    //   this.microTemporary.splice(index, 1);
    //   return index;
    // },

    addMicroToTemporaryZone(microAppName: string) {
      const microApp = this.microMap[microAppName];
      if (!microApp) {
        return;
      }
      microApp.group = 'more';
    },

    loadImMicro(elRef: HTMLElement) {
      const imMicro = this.imMicro;
      if (imMicro && elRef) {
        if (imMicro.activated) {
          // this.deactivateMicroApp('im');
          return;
        }
        return this.activateMicroApp('im', elRef, true);
      }
    },
    openImMicro() {
      const imMicro = this.sortingList.find((i) => i.name === 'message');
      if (!imMicro) {
        return;
      }
      imMicro.microDataRef.activated = true;
      if (!imMicro.microDataRef.activateAt) {
        imMicro.microDataRef.activateAt = new Date().getTime();
      }
      this.generateTemporaryOpenedAppList();
      this.showIm = true;
    },
    closeImMicro() {
      this.showIm = false;
      this.generateTemporaryOpenedAppList();
    },

    findMicroApp(microAppName: string) {
      const micro = this.microMap[microAppName];
      if (!micro) {
        console.warn(`can not find micro app =>`, microAppName);
        return;
      }
      return micro;
    },

    findMicroAppByEntry(entry: string) {
      const microapp = this.microList.find((i) => {
        if (!i.entry) {
          return;
        }
        const regStr = i.entry
          // .split('?')[0]
          .replace(/(\{[a-zA-Z0-9_\-.]+})/g, '(.+?)')
          .replace('?', '\\?');
        /*这里如果参数顺序不一致，匹配会出错*/
        const entryRegex = new RegExp(regStr);
        return entryRegex.test(entry);
      });
      return microapp;
    },

    addMicroTabItem(tabItem: TabItem) {
      let fullPath = tabItem.path || '';
      let [pathname, query] = fullPath.split('?');
      if (query || tabItem.query) {
        const q = qs.stringify({
          ...qs.parse(query || ''),
          ...tabItem.query,
        });
        if (q) {
          fullPath = `${pathname}?${q}`;
        } else {
          fullPath = pathname;
        }
      }
      if (tabItem.hash) {
        fullPath += tabItem.hash;
      }
      tabItem.fullPath = fullPath;
      const microData = this.microMap[tabItem.microName];
      if (!this.microTabMapping[tabItem.microName]) {
        this.microTabMapping[tabItem.microName] = [
          {
            fullPath: `${microData.path}${microData.initialHash}`,
            microName: microData.name,
            title: microData?.label,
            icon: microData?.icon,
          },
        ];
      }
      const history = this.microTabMapping[tabItem.microName];
      if (history.find((i) => i.fullPath === fullPath)) {
        return;
      }
      this.microTabMapping[tabItem.microName] = [
        ...this.microTabMapping[tabItem.microName],
        {
          ...tabItem,
          title: tabItem?.params?.title || microData?.label,
          icon: tabItem?.params?.icon || microData?.icon,
        },
      ];
    },

    findActiveTab(microName: string) {
      const fullPath = getFullPathFromLocation();
      const history = this.microTabMapping[microName];
      if (!history) {
        return;
      }
      return history.find((i) => i.fullPath === fullPath);
    },

    tabIsActive(tab?: TabItem) {
      const fullPath = getFullPathFromLocation();
      return tab?.fullPath === fullPath;
    },

    setMicroAppErrored(microAppName: string) {
      const microApp = this.microMap[microAppName];
      if (!microApp) {
        return;
      }
      microApp.errorCount = microApp.errorCount + 1;
    },

    removeTabItem(microName: string, index: number) {
      const routerStore = useGlobalRouteStore();
      if (!this.microTabMapping[microName]) {
        return;
      }
      const item = this.microTabMapping[microName][index];
      this.microTabMapping[microName].splice(index, 1);
      if (!this.tabIsActive(item)) {
        return;
      }
      const lastTab = last(this.microTabMapping[microName]);
      if (lastTab) {
        return routerStore.push(lastTab.fullPath as string);
      } else {
        this.removeMicroAppFromTemporaryZone(microName);
      }
    },

    removeAllTabs(microName: string) {
      const routerStore = useGlobalRouteStore();
      const micro = this.findMicroApp(microName);
      this.microTabMapping[microName] = [];
      if (!micro) return;
      routerStore.push(micro.fullPath as string);
    },

    microAppIsInFrequentlyZone(microAppName: string) {
      const micro = this.microMap[microAppName];

      return micro?.group === 'frequently';
    },

    setMicroAppToTemporaryZone(microAppName: string) {
      const micro = this.microMap[microAppName];
      micro.group = 'more';
      if (!this.openedTemporaryMicroAppNames.includes(micro.name)) {
        this.openedTemporaryMicroAppNames.push(micro.name);
      }
    },

    removeMicroAppFromFrequently(microAppName: string) {
      const microApp = this.sortingList.find((i) => i.name === microAppName);
      if (!microApp) {
        return;
      }
      microApp.microDataRef.group = 'more';
    },
    resetMicroAppHash(microAppName: string) {
      const microApp = this.microMap[microAppName];
      if (!microApp) {
        return;
      }
      microApp.hash = microApp.initialHash;
      microApp.latestUsedPath = undefined;
    },
    removeMicroAppFromTemporaryZone(microAppName: string) {
      let microApp;
      if (microAppName === 'message') {
        microApp = this.specialMicroApps.find((i) => i.name === 'message');
      } else if (microAppName === 'dashboard') {
        microApp = this.specialMicroApps.find((i) => i.name === 'dashboard');
      } else {
        microApp = this.microMap[microAppName];
      }
      if (!microApp) {
        return;
      }
      microApp.group = 'more';
      microApp.activateAt = undefined;
      this.generateTemporaryOpenedAppList();
      this.resetMicroAppHash(microAppName);

      if (this.openedTemporaryMicroAppNames.includes(microApp.name)) {
        this.openedTemporaryMicroAppNames =
          this.openedTemporaryMicroAppNames.filter((i) => i !== microApp.name);
      }
      if (microAppName === 'message') {
        microApp.activated = false;
        this.closeImMicro();
      } else {
        this.deactivateMicroApp(microAppName);
        this.resetMicroAppHash(microAppName);
        const routerStore = useGlobalRouteStore();
        routerStore.push('/');
      }
    },

    showOverloadingMask(microName: string) {
      let vnode = createVNode(OverloadMask, {});
      const micro = this.microMap[microName];
      const domEle = document.createElement('div');
      render(vnode, domEle);
      (micro.container as HTMLElement)?.appendChild(domEle);

      this.overloadingMaskContainerEl = domEle;
    },

    hideOverloadingMask() {
      if (this.overloadingMaskContainerEl) {
        this.overloadingMaskContainerEl.remove();
      }
    },

    async activateMicroApp(
      microAppName: string,
      containerEl?: HTMLElement,
      activeAsComponent?: boolean
    ) {
      const micro = this.microMap[microAppName];
      if (!micro) {
        console.warn(`can not find micro app =>`, microAppName);
        return;
      }
      if (micro.activated && activeAsComponent) {
        return;
      } else if (micro.activated && !activeAsComponent) {
        this.deactivateMicroApp(microAppName);
      }
      if (micro.microType === 'dashboard') {
        const dashboardApp = this.sortingList.find(
          (i) => i.name === 'dashboard'
        );
        if (dashboardApp) {
          dashboardApp.microDataRef.activated = true;
          dashboardApp.microDataRef.activateAt = new Date().getTime();
        }
      }
      if (containerEl) {
        micro.container = containerEl;
      }
      micro.activated = true;
      // 本次打开时间
      micro.startedAt = new Date().getTime();
      if (!micro.activateAt) {
        micro.activateAt = new Date().getTime();
      }
      this.generateTemporaryOpenedAppList();
      if (!activeAsComponent) {
        this.latestOpenedMicroAppName = microAppName;
      }
      if (!micro.isIframe) {
        try {
          micro._microAppInfo = this._loadMicroApp(microAppName);
        } catch (e) {
          console.error('loadMicroApp error', e);
        }
      }

      return micro;
    },

    deactivateMicroApp(microAppName: string) {
      const micro = this.microMap[microAppName];
      if (!micro) {
        console.warn(`can not find micro app =>`, microAppName);
        return;
      }
      if (this.latestOpenedMicroAppName === microAppName) {
        this.latestOpenedMicroAppName = undefined;
      }
      micro.activated = false;
      const startTime = micro.startedAt || 0;
      const duration =
        new Date().getTime() - startTime > 86400
          ? 86400
          : new Date().getTime() - startTime;
      Tracker.track('PageInDuration', {
        $event_duration: Math.round(duration / 1000),
        track_code: 'nio_business.b4326.pageViewTime.PageInDuration',
        requirement_id: 10470,
      });
      micro.mounted = false;
      if (micro._microAppInfo) {
        micro._microAppInfo.unmount();
      }
    },
    async reloadMicroApp(microAppName: string) {
      const micro = this.microMap[microAppName];
      if (!micro || !micro?._microAppInfo?.mount) {
        console.warn(`can not find micro app =>`, microAppName);
        return;
      }
      await micro._microAppInfo.unmount();
      await micro._microAppInfo.mount();
    },

    deactivateLatestOne() {
      if (!this.latestOpenedMicroAppName) {
        return;
      }
      this.deactivateMicroApp(this.latestOpenedMicroAppName);
    },

    deactivateAllMicroAPPs() {
      this.microList.forEach((microApp) => {
        this.deactivateMicroApp(microApp.name);
      });
    },
    _formatQianKunMicroAppOptions(microData: MicroData) {
      const globalStore = useGlobalStore();
      const messageHandler = inject('messageHandler');
      return {
        // name: microData.name + '-' + new Date().getTime(),
        name: microData.name,
        entry: microData.entry || '',
        container: microData.container || '',
        // @ts-ignore
        props: generateMicroProps({
          data: microData,
          userInfo: globalStore.userInfo,
          globalData: globalStore.global,
          availableBrands: globalStore.coreData.brands,
          brandAdmins: globalStore.coreData.brandAdmins,
          brandAccounts: globalStore.coreData.brandAccounts,
          brandAuthorityMapping: globalStore.coreData.brands.reduce((r, b) => {
            r[b] = globalStore.getBrandAuthorityMapping(b);
            return r;
          }, {}),
          sensorsInstance: Tracker,
          router: getVueRouter(),
        }),
      };
    },
    _precacheMicroApps(microAppEntryList) {
      const globalStore = useGlobalStore();
      prefetchApps(microAppEntryList, {
        fetch: generateFetch(),
      });
    },

    _loadMicroApp(microAppName: string) {
      const microApp = this.microMap[microAppName];
      const globalStore = useGlobalStore();
      if (!microApp) {
        console.error(`can not find micro app =>`, microAppName);
        throw new Error(`can not find micro app => ${microAppName}`);
      }
      if (!window.__TITAN_WEB_ORIGIN__) {
        window.__TITAN_WEB_ORIGIN__ = {};
      }
      try {
        window.__TITAN_WEB_ORIGIN__[microApp.name] = new URL(
          microApp.entry as string
        ).origin;
      } catch (e) {
        console.warn(`can not find micro app origin => ${microApp.name}`);
      }

      /*TODO: loadMicroApp beforeLoad*/
      // @ts-ignore
      const assetsBlackList = [
        ///https://static.nio.com/fx-static/lego-icon/fixed/iconfont_1.4.3.js
        'static.nio.com/fx-static/lego-icon/fixed/iconfont',
        /*
        //https://webapi.amap.com/maps?key=55f59****************b2afb&v=2.0&plugin=&callback=__onAMapLoaded0
        '.amap.com',
        */
      ];
      const qiankunMicroAppInfo = loadMicroApp(
        this._formatQianKunMicroAppOptions(microApp),
        {
          singular: false,
          fetch: generateFetch(),
          sandbox: {
            speedy: true,
            strictStyleIsolation: false, //  loadAppItem.styleIsolation,
            experimentalStyleIsolation: microApp.styleIsolation,
            // experimentalStyleIsolation: !['im/notice'].includes(microApp.name),
          },
          excludeAssetFilter: (assetUrl) => {
            const filtered = assetsBlackList.some((item) =>
              assetUrl.includes(item)
            );
            if (filtered) {
              console.warn('filtered assetUrl =>', assetUrl);
            }
            return filtered;
          },
        },
        {
          // @ts-ignore
          beforeLoad(app, windowProxy) {
            /*TODO: 需要测试这里是不是正常*/
            (windowProxy as any).sensorsDataAnalytic201505 = undefined;
            if (microAppName.includes('nsom')) {
              (windowProxy as any).__SENTRY__ = undefined;
            }
          },
          // @ts-ignore
          beforeMount: (app, windowProxy) => {
            console.log('beforeMount', microAppName);
          },
          // @ts-ignore
          afterMount: (app, windowProxy) => {
            console.log('mounted', microAppName);
            microApp.mounted = true;
          },
          // @ts-ignore
          beforeUnmount: (app, windowProxy) => {
            if (microAppName.includes('nsom')) {
              (windowProxy as any).__SENTRY__ = undefined;
            }
          },
        }
      );
      qiankunMicroAppInfo?.mountPromise
        .then(() => {
          console.log(`%c mountPromise: ${microAppName}`, 'color: blue');
        })
        .catch((err: Error) => {
          console.error(`mountPromise err:`, err);
        })
        .finally(() => {
          NProgress.done();
        });

      qiankunMicroAppInfo?.unmountPromise
        .then(() => {
          console.log(`%c unmountPromise: ${microAppName}`, 'color: blue');
        })
        .catch((err: Error) => {
          console.error(`unmountPromise err:`, err);
        })
        .finally(() => {
          NProgress.done();
        });

      return qiankunMicroAppInfo;
    },
  },
  persist: {
    key: 'tit-matestore-v2',
    storage: window.sessionStorage,
    paths: ['microTabMapping', 'temporaryOpenedAppList'],
  },
});

export default useMatestoreStore;
export type IMatestoreStore = ReturnType<typeof useMatestoreStore>;
